import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,

} from '@mui/material';
import {
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  CloudUpload as CloudUploadIcon,

} from '@mui/icons-material';
import { questionAPI, migrationAPI } from '../services/api';
import { SubjectEnum, QuestionType } from '../types';
import { MigrationResult } from '../types/migration';

interface QuestionImportExportProps {
  open: boolean;
  onClose: () => void;
  mode: 'import' | 'export';
  knowledgePointId?: number;
  onImportSuccess?: () => void;
}

const QuestionImportExport: React.FC<QuestionImportExportProps> = ({
  open,
  onClose,
  mode,
  knowledgePointId,
  onImportSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<MigrationResult | null>(null);

  // 导入相关状态
  const [jsonContent, setJsonContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 导出相关状态
  const [exportFilters, setExportFilters] = useState({
    knowledgePointId: undefined as number | undefined,
    questionType: '' as QuestionType | '',
    subject: '' as SubjectEnum | '',
  });

  // 处理文件上传
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      setError('请选择JSON格式的文件');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        // 基础JSON格式验证
        JSON.parse(content);
        setJsonContent(content);
        setError(null);
      } catch (err) {
        setError('JSON格式不正确，请检查文件内容');
      }
    };
    reader.readAsText(file);
  };

  // 处理JSON内容变化
  const handleJsonContentChange = (content: string) => {
    setJsonContent(content);
    setError(null);
  };

  // 处理导入 - 前端分隔并循环调用导入接口（同步）
  const handleImport = async () => {
    if (!jsonContent.trim()) {
      setError('请输入或上传题目JSON数据');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const trimmed = jsonContent.trim();
      let items: any[] | null = null;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) items = parsed;
        else if (typeof parsed === 'object') items = [parsed];
      } catch (_) { /* 非数组/object JSON */ }

      let total = 0;
      let errors = 0;
      const errs: string[] = [];

      if (items) {
        total = items.length;
        for (let i = 0; i < items.length; i++) {
          try {
            const it = items[i];
            const resp = typeof it === 'string'
              ? await migrationAPI.migrateSingleText(it)
              : await migrationAPI.migrateSingleObject(it);
            if (!resp.success) {
              errors++;
              errs.push(resp.message || `第${i + 1}条导入失败`);
            }
          } catch (e: any) {
            errors++;
            errs.push(e?.message || `第${i + 1}条导入异常`);
          }
        }
      } else {
        // 非数组，整段交给后端一次性处理
        const resp = await migrationAPI.migrateFromJsonString(trimmed);
        if (!resp.success) throw new Error(resp.message || '导入失败');
        setImportResult(resp.data);
        onImportSuccess?.();
        setLoading(false);
        return;
      }

      const result = {
        success: errors === 0,
        totalCount: total,
        successCount: total - errors,
        errorCount: errors,
        skipCount: 0,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        durationMs: 0,
        errors: errs,
        message: errors === 0 ? '导入成功' : '部分失败',
      } as any;
      setImportResult(result);
      if (total - errors > 0) onImportSuccess?.();
    } catch (err: any) {
      console.error('导入失败:', err);
      setError(err.message || '导入失败，请检查数据格式');
    } finally {
      setLoading(false);
    }
  };

  // 处理导出
  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        knowledgePointId: exportFilters.knowledgePointId,
        questionType: exportFilters.questionType || undefined,
        subject: exportFilters.subject || undefined,
      };

      console.log('导出参数:', params);

      const data = await questionAPI.exportQuestions(params);

      console.log('导出数据:', data);

      if (!data || (Array.isArray(data) && data.length === 0)) {
        setError('没有找到符合条件的题目');
        return;
      }

      // 创建下载链接
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `questions_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onClose();
    } catch (err: any) {
      console.error('导出失败:', err);
      if (err.response) {
        console.error('响应错误:', err.response.data);
        setError(`导出失败: ${err.response.data.message || err.response.statusText}`);
      } else if (err.request) {
        setError('网络错误：无法连接到服务器');
      } else {
        setError(err.message || '导出失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 重置状态
  const handleClose = () => {
    setJsonContent('');
    setError(null);
    setImportResult(null);
    setExportFilters({
      knowledgePointId: undefined,
      questionType: '',
      subject: '',
    });
    onClose();
  };

  // 数据迁移示例JSON数据（支持material字段映射到content）
  const exampleJson = `[
  {
    "type": "SINGLE_CHOICE",
    "id": 472453,
    "subject": "MATH",
    "difficulty": "MEDIUM",
    "content": "<div>一个八位数，它的最高位是 ____  .  </div>",
    "answer": "B",
    "options": [
      "百万位",
      "千万位",
      "千位",
      "十万位"
    ],
    "explanation": "<div>根据个、十、百、千、万、十万、百万、千万、亿的顺序，八位数的最高位是千万位，故选B.</div>",
    "basicInfo": {
      "subject": {"id": 1, "name": "数学"},
      "bookversion": {"id": "82013", "name": "小学西师大版", "grade": "四年级上"},
      "chapter": {"id": 2205429, "name": "期末测评"},
      "knowledgePoints": [{"id": 6000033266, "name": "数位顺序表"}]
    }
  },
  {
    "type": "MULTIPLE_CHOICE",
    "id": 1640995260000,
    "subject": "MATH",
    "difficulty": "MEDIUM",
    "tags": ["数论", "质数"],
    "content": "<p>下列哪些是质数？</p>",
    "options": [
      "<p>2</p>",
      "<p>3</p>",
      "<p>4</p>",
      "<p>5</p>",
      "<p>6</p>"
    ],
    "answer": "A,B,D",
    "explanation": "<p>质数是只能被1和自身整除的大于1的自然数。2、3、5是质数，4和6不是。</p>",
    "remarks": "多选题示例 - 数学概念"
  },
  {
    "type": "TRUE_FALSE",
    "id": 1640995320000,
    "subject": "PHYSICS",
    "difficulty": "EASY",
    "tags": ["光学", "基本常数"],
    "content": "<p>光在真空中的传播速度是3×10<sup>8</sup>m/s。</p>",
    "answer": "true",
    "explanation": "<p>这是物理学中的基本常数，光在真空中的传播速度确实是3×10<sup>8</sup>m/s。</p>",
    "remarks": "判断题示例 - 物理常识"
  },
  {
    "type": "READING_COMPREHENSION",
    "id": 1640995380000,
    "subject": "ENGLISH",
    "difficulty": "MEDIUM",
    "tags": ["阅读理解", "路线指引"],
    "content": "<p>阅读下面的信件，回答问题。</p>",
    "material": "<div><p>Dear Betty,</p><p>Thank you very much for your email. I know you'd like to visit the National Museum in our city. It is a big and famous museum. Let me tell you the way to it. Take a car from your house and drive along Hongxing Street. Turn left at the third turning, not the last one. You'll see a park on your right. The museum is opposite the park.</p><p>Yours, Jerry</p></div>",
    "subQuestions": [
      {
        "id": 1,
        "type": "SINGLE_CHOICE",
        "content": "<p>Betty will go to the National Museum ____.</p>",
        "options": [
          "<p>by bus</p>",
          "<p>by taxi</p>",
          "<p>by car</p>",
          "<p>by bike</p>"
        ],
        "answer": "C",
        "explanation": "<p>根据短文中'Take a car from your house'可知贝蒂是坐车去博物馆的。</p>"
      }
    ],
    "remarks": "阅读理解题示例 - 复合题型"
  }
]`;



  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            {mode === 'import' ? '导入题目' : '导出题目'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {mode === 'import' ? '正在导入题目...' : '正在导出题目...'}
            </Typography>
          </Box>
        )}

        {mode === 'import' ? (
          <Box>

            {/* 文件上传 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                上传JSON文件
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                选择文件
              </Button>
            </Box>

            <Divider sx={{ my: 2 }}>或</Divider>

            {/* 直接输入JSON */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                直接输入JSON数据
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={12}
                placeholder="请输入题目JSON数据..."
                value={jsonContent}
                onChange={(e) => handleJsonContentChange(e.target.value)}
                sx={{ fontFamily: 'monospace' }}
              />
            </Box>

            {/* 示例数据 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                数据迁移JSON格式示例（支持material字段）：
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <pre>{exampleJson}</pre>
              </Box>
              <Button
                size="small"
                onClick={() => handleJsonContentChange(exampleJson)}
                sx={{ mt: 1 }}
              >
                使用示例数据
              </Button>
            </Box>



            {/* 导入结果 */}
            {importResult && (
              <Alert
                severity={importResult.errorCount > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" gutterBottom>
                  数据迁移完成：成功 {importResult.successCount} 个，失败 {importResult.errorCount} 个，总计 {importResult.totalCount} 个
                </Typography>
                <Typography variant="body2" gutterBottom>
                  耗时：{importResult.durationMs}ms，成功率：{((importResult.successCount / importResult.totalCount) * 100).toFixed(1)}%
                </Typography>
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      错误信息：
                    </Typography>
                    <List dense>
                      {importResult.errors.map((error, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemText
                            primary={error}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                {importResult.message && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {importResult.message}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            {/* 导出筛选条件 */}
            <Typography variant="subtitle1" gutterBottom>
              导出筛选条件
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
              <TextField
                label="知识点ID"
                type="number"
                value={exportFilters.knowledgePointId || ''}
                onChange={(e) => setExportFilters(prev => ({
                  ...prev,
                  knowledgePointId: e.target.value ? Number(e.target.value) : undefined
                }))}
                helperText="留空导出所有知识点"
              />
              
              <FormControl>
                <InputLabel>题型</InputLabel>
                <Select
                  value={exportFilters.questionType}
                  label="题型"
                  onChange={(e) => setExportFilters(prev => ({
                    ...prev,
                    questionType: e.target.value as QuestionType
                  }))}
                >
                  <MenuItem value="">全部题型</MenuItem>
                  <MenuItem value="SINGLE_CHOICE">单选题</MenuItem>
                  <MenuItem value="MULTIPLE_CHOICE">多选题</MenuItem>
                  <MenuItem value="FILL_IN_BLANK">填空题</MenuItem>
                  <MenuItem value="TRUE_FALSE">判断题</MenuItem>
                  <MenuItem value="MATCHING">匹配题</MenuItem>
                  <MenuItem value="READING_COMPREHENSION">阅读理解</MenuItem>
                  <MenuItem value="CLOZE_TEST">完形填空</MenuItem>
                  <MenuItem value="LISTENING">听力题</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl>
                <InputLabel>科目</InputLabel>
                <Select
                  value={exportFilters.subject}
                  label="科目"
                  onChange={(e) => setExportFilters(prev => ({
                    ...prev,
                    subject: e.target.value as SubjectEnum
                  }))}
                >
                  <MenuItem value="">全部科目</MenuItem>
                  <MenuItem value="ENGLISH">英语</MenuItem>
                  <MenuItem value="MATH">数学</MenuItem>
                  <MenuItem value="PHYSICS">物理</MenuItem>
                  <MenuItem value="CHEMISTRY">化学</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Alert severity="info">
              导出的JSON文件可以直接用于题目导入功能
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={mode === 'import' ? handleImport : handleExport}
          disabled={loading || (mode === 'import' && !jsonContent.trim())}
          startIcon={mode === 'import' ? <FileUploadIcon /> : <FileDownloadIcon />}
        >
          {mode === 'import' ? '导入' : '导出'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionImportExport;
